import type {
  Role,
  RolesAction,
  Privilege,
  Concern,
  ErrorMessagesByType,
} from 'src/action-creators/roles';

import keyBy from 'lodash/keyBy';
import difference from 'lodash/difference';
import union from 'lodash/union';
import omit from 'lodash/omit';

import {
  RECEIVE,
  RECEIVE_ROLE,
  REMOVE_ROLE,
  START_EDITING,
  START_EDITING_ERROR,
  CLEAR_EDITING,
  MARK_ERRORS_SHOWABLE,
  EDIT_ROLE_NAME,
  EDIT_ROLE_PRIVILEGES,
  EDIT_ROLE_CONFIGURATION,
  REMOVE_ROLE_PRIVILEGE,

  // Privileges
  VIEW_WORKFLOWS,
  EDIT_WORKFLOWS,
  VIEW_AUDIENCE_MEMBERS,
  EDIT_AUDIENCE_MEMBERS,
  VIEW_ANALYTICS,
  EDIT_SETTINGS,
  EDIT_PERMISSIONS,
  USE_MESSAGING,
  MANAGE_OPT_OUTS,
  EDIT_CHATBOT,
  USE_VOICEFLOW,
  ADMIN_REFERRALS,
  RECRUITER_REFERRALS,
  MANAGE_CALENDAR,
  MANAGE_TEAM_TEMPLATES,
  MANAGE_HOST,
  MANAGE_USER_MANAGEMENT_DASHBOARD,
  MANAGE_USER_IMPERSONATION,
  MANAGE_USER_CALENDAR_CONNECTION,
  TRM_ADMIN,
  TRM_VIEW,
  VIEW_REFERRALS_V2,
  EDIT_REFERRALS_V2,
  MANAGE_SECURITY,
  ADMIN_REFERRALS_V2,
  USE_JD_GENERATOR,
  USE_PRESCREENING_GENERATOR,
  USE_RESUME_GENERATOR,
  USE_ENGAGE_CONTENT_GENERATOR,
  USE_MESSAGING_SMS_GENERATOR,
  PAGES,
  LEADS,
  VIEW_PAGES,
  EDIT_PAGES,
  ADMIN_PAGES,
  VIEW_LEAD_MANAGEMENT,
  ADMIN_LEAD_MANAGEMENT,
  EDIT_LEAD_MANAGEMENT,
  VIEW_AEP_WORKFLOWS,
  EDIT_AEP_WORKFLOWS,
  ACTIVATE_AEP_WORKFLOWS,
  USE_SURVEY_SUMMARY_GENERATOR,
  VIEW_JOBS,
  ADMIN_JOBS,
  VIEW_CREDENTIALS,
  EDIT_CREDENTIALS,
  VIEW_SURVEYS,
  EDIT_SURVEYS,
  VIEW_AI_RECRUITER,
} from 'src/action-creators/roles';
import {VIEW_RECORDS} from '../types/roles';


export const noAccess = 'noAccess';
export const view = 'view';
export const edit = 'edit';
export const use = 'use';
export const manage_opt_opts = 'manage_opt_opts';

export const manage_own_calendar = 'manage_own_calendar';
export const manage_own_and_team_calendars = 'manage_own_and_team_calendars';
export const manage_host = 'manage_host';
export const manage_user_dashboard = 'manage_user_dashboard';
export const manage_user_impersonation = 'manage_user_impersonation';
export const manage_user_calendar_connection =
  'manage_user_calendar_connection';

export const admin = 'admin';
export const jd_generator = 'jd_generator';
export const prescreening_generator = 'prescreening_generator';
export const resume_generator = 'resume_generator';
export const engage_content_generator = 'engage_content_generator';
export const messaging_sms_generator = 'messaging_sms_generator';
export const survey_summary_generator = 'survey_summary_generator';
export const activate_automation_workflow = 'activate_automation_workflow';

type AccessLevelsByConcern = Partial<
  Record<
    Concern,
    {
      [level: string]: {
        add: Privilege[];
        remove: Privilege[];
      };
    }
  >
>;

export const accessLevelsByConcern: AccessLevelsByConcern = {
  WORKFLOWS: {
    [noAccess]: {add: [], remove: [VIEW_WORKFLOWS, EDIT_WORKFLOWS]},
    [view]: {add: [VIEW_WORKFLOWS], remove: [EDIT_WORKFLOWS]},
    [edit]: {add: [VIEW_WORKFLOWS, EDIT_WORKFLOWS], remove: []},
  },
  ANALYTICS: {
    [noAccess]: {add: [], remove: [VIEW_ANALYTICS]},
    [view]: {add: [VIEW_ANALYTICS], remove: []},
  },
  AUDIENCE_MEMBERS: {
    [noAccess]: {
      add: [],
      remove: [VIEW_AUDIENCE_MEMBERS, EDIT_AUDIENCE_MEMBERS],
    },
    [view]: {add: [VIEW_AUDIENCE_MEMBERS], remove: [EDIT_AUDIENCE_MEMBERS]},
    [edit]: {add: [EDIT_AUDIENCE_MEMBERS, VIEW_AUDIENCE_MEMBERS], remove: []},
  },
  MESSAGING: {
    [noAccess]: {add: [], remove: [USE_MESSAGING]},
    [use]: {add: [USE_MESSAGING], remove: []},
  },
  MANAGE_OPT_OUTS: {
    [noAccess]: {add: [], remove: [MANAGE_OPT_OUTS]},
    [manage_opt_opts]: {add: [MANAGE_OPT_OUTS], remove: []},
  },
  CHATBOT: {
    [noAccess]: {add: [], remove: [EDIT_CHATBOT]},
    [edit]: {add: [EDIT_CHATBOT], remove: []},
  },
  VOICEFLOW: {
    [noAccess]: {add: [], remove: [USE_VOICEFLOW]},
    [edit]: {add: [USE_VOICEFLOW], remove: []},
  },
  SETTINGS: {
    [noAccess]: {add: [], remove: [EDIT_SETTINGS]},
    [edit]: {add: [EDIT_SETTINGS], remove: []},
  },
  PERMISSIONS: {
    [noAccess]: {add: [], remove: [EDIT_PERMISSIONS]},
    [edit]: {add: [EDIT_PERMISSIONS], remove: []},
  },
  REFERRAL: {
    [noAccess]: {add: [], remove: [ADMIN_REFERRALS, RECRUITER_REFERRALS]},
    [view]: {add: [RECRUITER_REFERRALS], remove: [ADMIN_REFERRALS]},
    [edit]: {add: [ADMIN_REFERRALS], remove: []},
  },
  SCHEDULER: {
    [noAccess]: {
      add: [],
      remove: [
        MANAGE_CALENDAR,
        MANAGE_TEAM_TEMPLATES,
        MANAGE_HOST,
        MANAGE_USER_MANAGEMENT_DASHBOARD,
        MANAGE_USER_IMPERSONATION,
        MANAGE_USER_CALENDAR_CONNECTION,
      ],
    },
    [manage_own_calendar]: {
      add: [MANAGE_CALENDAR],
      remove: [
        MANAGE_TEAM_TEMPLATES,
        MANAGE_HOST,
        MANAGE_USER_IMPERSONATION,
        MANAGE_USER_CALENDAR_CONNECTION,
        MANAGE_USER_MANAGEMENT_DASHBOARD,
      ],
    },
    [manage_own_and_team_calendars]: {
      add: [MANAGE_CALENDAR, MANAGE_TEAM_TEMPLATES],
      remove: [
        MANAGE_HOST,
        MANAGE_USER_IMPERSONATION,
        MANAGE_USER_CALENDAR_CONNECTION,
        MANAGE_USER_MANAGEMENT_DASHBOARD,
      ],
    },
    [manage_host]: {
      add: [MANAGE_HOST],
      remove: [],
    },
    [manage_user_dashboard]: {
      add: [MANAGE_USER_MANAGEMENT_DASHBOARD],
      remove: [],
    },
    [manage_user_impersonation]: {
      add: [MANAGE_USER_IMPERSONATION],
      remove: [],
    },
    [manage_user_calendar_connection]: {
      add: [MANAGE_USER_CALENDAR_CONNECTION],
      remove: [],
    },
  },
  TRM: {
    [noAccess]: {add: [], remove: [TRM_ADMIN, TRM_VIEW]},
    [view]: {add: [TRM_VIEW], remove: [TRM_ADMIN]},
    [edit]: {add: [TRM_ADMIN], remove: []},
  },
  REFERRAL_V2: {
    [noAccess]: {
      add: [],
      remove: [VIEW_REFERRALS_V2, EDIT_REFERRALS_V2, ADMIN_REFERRALS_V2],
    },
    [view]: {
      add: [VIEW_REFERRALS_V2],
      remove: [EDIT_REFERRALS_V2, ADMIN_REFERRALS_V2],
    },
    [edit]: {
      add: [VIEW_REFERRALS_V2, EDIT_REFERRALS_V2],
      remove: [ADMIN_REFERRALS_V2],
    },
    [admin]: {
      add: [VIEW_REFERRALS_V2, EDIT_REFERRALS_V2, ADMIN_REFERRALS_V2],
      remove: [],
    },
  },
  SECURITY: {
    [noAccess]: {add: [], remove: [MANAGE_SECURITY]},
    [edit]: {add: [MANAGE_SECURITY], remove: []},
  },
  SENSE_AI: {
    [jd_generator]: {add: [USE_JD_GENERATOR], remove: []},
    [prescreening_generator]: {add: [USE_PRESCREENING_GENERATOR], remove: []},
    [resume_generator]: {add: [USE_RESUME_GENERATOR], remove: []},
    [engage_content_generator]: {
      add: [USE_ENGAGE_CONTENT_GENERATOR],
      remove: [],
    },
    [messaging_sms_generator]: {add: [USE_MESSAGING_SMS_GENERATOR], remove: []},
    [survey_summary_generator]: {
      add: [USE_SURVEY_SUMMARY_GENERATOR],
      remove: [],
    },
  },
  PAGES: {
    [noAccess]: {
      add: [],
      remove: [VIEW_PAGES, EDIT_PAGES, ADMIN_PAGES],
    },
    [view]: {
      add: [VIEW_PAGES],
      remove: [EDIT_PAGES, ADMIN_PAGES],
    },
    [edit]: {
      add: [EDIT_PAGES, VIEW_PAGES],
      remove: [ADMIN_PAGES],
    },
    [admin]: {add: [ADMIN_PAGES, EDIT_PAGES, VIEW_PAGES], remove: []},
  },
  LEADS: {
    [noAccess]: {
      add: [],
      remove: [
        VIEW_LEAD_MANAGEMENT,
        ADMIN_LEAD_MANAGEMENT,
        EDIT_LEAD_MANAGEMENT,
      ],
    },
    [view]: {
      add: [VIEW_LEAD_MANAGEMENT],
      remove: [EDIT_LEAD_MANAGEMENT, ADMIN_LEAD_MANAGEMENT],
    },
    [edit]: {
      add: [EDIT_LEAD_MANAGEMENT, VIEW_LEAD_MANAGEMENT],
      remove: [ADMIN_LEAD_MANAGEMENT],
    },
    [admin]: {
      add: [EDIT_LEAD_MANAGEMENT, VIEW_LEAD_MANAGEMENT, ADMIN_LEAD_MANAGEMENT],
      remove: [],
    },
  },
  AUTOMATION_WORKFLOWS: {
    [noAccess]: {
      add: [],
      remove: [VIEW_AEP_WORKFLOWS, EDIT_AEP_WORKFLOWS, ACTIVATE_AEP_WORKFLOWS],
    },
    [view]: {
      add: [VIEW_AEP_WORKFLOWS],
      remove: [EDIT_AEP_WORKFLOWS, ACTIVATE_AEP_WORKFLOWS],
    },
    [edit]: {
      add: [VIEW_AEP_WORKFLOWS, EDIT_AEP_WORKFLOWS],
      remove: [ACTIVATE_AEP_WORKFLOWS],
    },
    [activate_automation_workflow]: {
      add: [VIEW_AEP_WORKFLOWS, EDIT_AEP_WORKFLOWS, ACTIVATE_AEP_WORKFLOWS],
      remove: [],
    },
  },
  JOBS: {
    [noAccess]: {
      add: [],
      remove: [VIEW_JOBS, ADMIN_JOBS],
    },
    [view]: {
      add: [VIEW_JOBS],
      remove: [ADMIN_JOBS],
    },
    [admin]: {
      add: [ADMIN_JOBS, VIEW_JOBS],
      remove: [],
    },
  },
  CREDENTIALS: {
    [edit]: {add: [VIEW_CREDENTIALS, EDIT_CREDENTIALS], remove: []},
    [noAccess]: {add: [], remove: [VIEW_CREDENTIALS, EDIT_CREDENTIALS]},
    [view]: {add: [VIEW_CREDENTIALS], remove: [EDIT_CREDENTIALS]},
  },
  SURVEY: {
    [noAccess]: {add: [], remove: [VIEW_SURVEYS, EDIT_SURVEYS]},
    [view]: {add: [VIEW_SURVEYS], remove: [EDIT_SURVEYS]},
    [edit]: {add: [VIEW_SURVEYS, EDIT_SURVEYS], remove: []},
  },
  RECORDS: {
    [noAccess]: {add: [], remove: [VIEW_RECORDS]},
    [view]: {add: [VIEW_RECORDS], remove: []},
  },
  AI_RECRUITER: {
    [noAccess]: {add: [], remove: [VIEW_AI_RECRUITER]},
    [view]: {add: [VIEW_AI_RECRUITER], remove: []},
  },
};

const nextPrivileges = (
  prevPrivileges: Array<Privilege>,
  concern: Concern,
  level: string,
): Privilege[] => {
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  const {add, remove} = accessLevelsByConcern[concern][level];
  return union(difference(prevPrivileges, remove), add);
};

type RolesById = {
  [id: string]: Role;
};

export type RolesState = {
  data: RolesById;
  editing: Role | null | undefined;
  errors: ErrorMessagesByType;
  showErrors: boolean;
};

const initialState = {
  data: {},
  editing: null,
  errors: {},
  showErrors: false,
} as const;

const validateRole = (
  role:
    | Role
    | {
        name: string;
      },
): ErrorMessagesByType => {
  if (!role) {
    return {};
  }

  const {name} = role;
  const errors: Record<string, any> = {};
  if (name.trim().length === 0) {
    errors.name = ['Enter a name for this role'];
  }
  return errors;
};

export default (
  state: RolesState = initialState,
  action: RolesAction,
): RolesState => {
  switch (action.type) {
    case RECEIVE:
      return {...state, data: keyBy(action.payload, (role) => role.id)};

    case RECEIVE_ROLE:
      return {
        ...state,
        data: {
          ...state.data,
          [action.payload.id]: action.payload,
        },
      };

    case REMOVE_ROLE:
      return {...state, data: omit(state.data, action.payload)};

    case START_EDITING:
      return {
        ...state,
        editing: action.payload,
        errors: validateRole(action.payload),
        showErrors: false,
      };

    case START_EDITING_ERROR:
      return {...state, editing: null, errors: action.payload};

    case CLEAR_EDITING:
      return {...state, editing: null, showErrors: false};

    case MARK_ERRORS_SHOWABLE:
      return {...state, showErrors: true};

    case EDIT_ROLE_NAME: {
      const currentRole = state.editing;
      if (!currentRole) {
        return state;
      }

      const errors = validateRole(action.payload);
      return {
        ...state,
        errors,
        showErrors: Object.values(errors).length > 0,

        editing: {
          ...currentRole,
          name: action.payload.name,
        },
      };
    }

    case EDIT_ROLE_PRIVILEGES: {
      if (!state.editing) {
        return state;
      }

      const {privileges} = state.editing;
      const {concern, level} = action.payload;
      return {
        ...state,
        editing: {
          ...state.editing,
          privileges: nextPrivileges(privileges, concern, level),
        },
      };
    }

    case EDIT_ROLE_CONFIGURATION: {
      if (!state.editing) {
        return state;
      }

      const {configurations} = state.editing;
      const {configurationKey, value} = action.payload;
      return {
        ...state,
        editing: {
          ...state.editing,
          configurations: {
            ...configurations,
            [configurationKey]: value,
          },
        },
      };
    }
    case REMOVE_ROLE_PRIVILEGE: {
      if (!state.editing) {
        return state;
      }

      const {privilege} = action.payload;
      return {
        ...state,
        editing: {
          ...state.editing,
          privileges: difference(state.editing.privileges, [privilege]),
        },
      };
    }
  }

  return state;
};
